import { useCallback, useRef, useEffect } from 'react';
import { useAuthContext } from './useAuthContext';
import memberActivityAPI from '../services/member_activity';

// Activity tracking configuration
const ACTIVITY_DEBOUNCE_MS = 15000; // 15 seconds - minimum time between activity updates
const ACTIVITY_THROTTLE_MS = 5000;  // 5 seconds - minimum time between any activity calls
const MAX_RETRIES = 3;

/**
 * Custom hook for tracking member activity with debouncing and throttling
 * to prevent overloading the backend server
 */
export function useMemberActivityTracking() {
  const { currentMembership, account } = useAuthContext();
  const lastActivityTimeRef = useRef(0);
  const lastUpdateTimeRef = useRef(0);
  const retryCountRef = useRef(0);
  const timeoutRef = useRef(null);

  /**
   * Updates member's last_activity_at on the backend
   */
  const updateMemberActivity = useCallback(async (memberId, schoolId, accessToken) => {
    
    if (!memberId || !schoolId) {
      //console.log('updateMemberActivity: Missing required parameters, returning early');
      return;
    }

    const now = Date.now();
    
    // Throttle: Don't make calls more than once every 5 seconds
    if (now - lastUpdateTimeRef.current < ACTIVITY_THROTTLE_MS) {
      //console.log('updateMemberActivity: Throttled, too soon since last update');
      return;
    }

    // Debounce: Only update if enough time has passed since last activity update
    if (now - lastActivityTimeRef.current < ACTIVITY_DEBOUNCE_MS) {
      //console.log('updateMemberActivity: Debounced, too soon since last activity update');
      return;
    }

    //console.log('updateMemberActivity: Making API call');
    lastUpdateTimeRef.current = now;

    try {
      await memberActivityAPI.updateMemberActivity(schoolId, memberId, accessToken);
      //console.log('updateMemberActivity: Success!');
      lastActivityTimeRef.current = now;
      retryCountRef.current = 0; // Reset retry count on success
    } catch (error) {
      console.warn('Failed to update member activity:', error);
      
      // Implement exponential backoff for retries
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        const retryDelay = Math.pow(2, retryCountRef.current) * 1000; // Exponential backoff
        
        setTimeout(() => {
          updateMemberActivity(memberId, schoolId, accessToken);
        }, retryDelay);
      }
    }
  }, []);

  /**
   * Tracks activity with debouncing - only updates if enough time has passed
   */
  const trackActivity = useCallback((accessToken) => {

    //console.log('trackActivity called with:', { 
    //  currentMembership, 
    //  member_id: currentMembership?.id, 
    //  school_id: currentMembership?.school_id 
    //});
        
    if (!currentMembership?.id || !currentMembership?.school_id) {
      //console.log('trackActivity: Missing required data, returning early');
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    //console.log('trackActivity: Setting timeout for activity update');
    // Set a new timeout to update activity after debounce period
    timeoutRef.current = setTimeout(() => {
      //console.log('trackActivity: Timeout fired, calling updateMemberActivity');
      updateMemberActivity(
        currentMembership.id,
        currentMembership.school_id,
        accessToken
      );
    }, ACTIVITY_DEBOUNCE_MS);
  }, [currentMembership, updateMemberActivity]);

  /**
   * Force immediate activity update (bypasses debouncing)
   */
  const forceActivityUpdate = useCallback((accessToken) => {
    if (!currentMembership || !currentMembership?.school_id) {
      return;
    }

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    updateMemberActivity(
      currentMembership.id,
      currentMembership.school_id,
      accessToken
    );
  }, [currentMembership, updateMemberActivity]);

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    trackActivity,
    forceActivityUpdate,
    updateMemberActivity,
  };
}
